/** Pure helpers behind `ui/AutoTooltip` (kept free of React for the unit tests). */

const GAP = 6;
const EDGE = 8;
const TIP_H = 28;

/**
 * Where the tip goes: centred above the control, or below it when there is no
 * room above (top bar, first rows). `left` is the tip's centre, clamped so a
 * typical tip stays inside the viewport.
 */
export function placeTip(r: { top: number; bottom: number; left: number; width: number }, viewportW: number, halfW = 60) {
  const below = r.top - GAP - TIP_H < EDGE;
  const centre = r.left + r.width / 2;
  const left = Math.min(Math.max(centre, EDGE + halfW), viewportW - EDGE - halfW);
  return { top: below ? r.bottom + GAP : r.top - GAP, left, below };
}

/** Visible text of an element (sr-only text and hidden nodes do not count). */
export function hasVisibleText(el: Element): boolean {
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!n.textContent?.trim()) continue;
    const parent = n.parentElement;
    if (!parent) continue;
    const box = parent.getBoundingClientRect();
    if (box.width > 2 && box.height > 2) return true;
  }
  return false;
}

/**
 * The text to show for a control, or null when it does not qualify:
 * opted out, inside a wrapper that has its own tooltip, or it has visible text.
 */
export function tipLabel(el: HTMLElement): string | null {
  if (el.closest('[data-no-tooltip], [data-tooltip-owner]')) return null;
  const custom = el.getAttribute('data-tip');
  if (custom) return custom;
  if (hasVisibleText(el)) return null;
  const name = el.getAttribute('aria-label') || el.getAttribute('title') || el.textContent;
  const text = name?.replace(/\s+/g, ' ').trim();
  return text ? text : null;
}
